import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { useConfirmation } from "@/components/ConfirmationProvider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Plus, Calendar as CalendarIcon, Clock, Edit2, Trash2, User, Stethoscope } from "lucide-react";
import { format, parseISO, isSameDay } from "date-fns";

const STATUS_OPTIONS = ["scheduled", "completed", "cancelled"];

export default function AppointmentsPage() {
  const confirm = useConfirmation();
  const [appointments, setAppointments] = useState([]);
  const [goals, setGoals] = useState([]);
  const [activities, setActivities] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [children, setChildren] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAppt, setEditingAppt] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [form, setForm] = useState({
    child_id: "",
    provider_id: "",
    title: "",
    date: "",
    time: "",
    duration_minutes: 60,
    notes: "",
    status: "scheduled"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [apptsRes, childrenRes, providersRes, goalsRes, activitiesRes, budgetsRes] = await Promise.all([
        apiClient.get("/appointments"),
        apiClient.get("/children"),
        apiClient.get("/providers"),
        apiClient.get("/goals").catch(() => ({ data: [] })),
        apiClient.get("/activities").catch(() => ({ data: [] })),
        apiClient.get("/budgets").catch(() => ({ data: [] }))
      ]);
      setAppointments(apptsRes.data);
      setChildren(childrenRes.data);
      setProviders(providersRes.data);
      setGoals(goalsRes.data || []);
      setActivities(activitiesRes.data || []);
      setBudgets(budgetsRes.data || []);
    } catch (error) {
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAppt) {
        await apiClient.put(`/appointments/${editingAppt.id}`, form);
        toast.success("Appointment updated!");
      } else {
        await apiClient.post("/appointments", form);
        toast.success("Appointment created!");
      }
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: "Delete appointment?", description: "This appointment will be permanently removed." }))) return;
    try {
      await apiClient.delete(`/appointments/${id}`);
      toast.success("Appointment deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const openEdit = (appt) => {
    setEditingAppt(appt);
    setForm({
      child_id: appt.child_id,
      provider_id: appt.provider_id,
      title: appt.title,
      date: appt.date,
      time: appt.time,
      duration_minutes: appt.duration_minutes,
      notes: appt.notes,
      status: appt.status
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setForm({
      child_id: "",
      provider_id: "",
      title: "",
      date: "",
      time: "",
      duration_minutes: 60,
      notes: "",
      status: "scheduled"
    });
    setEditingAppt(null);
    setDialogOpen(false);
  };

  const getChildName = (id) => children.find(c => c.id === id)?.name || "Unknown";
  const getProviderName = (id) => providers.find(p => p.id === id)?.name || "Unknown";

  // Build unified event list: appointments + goal target dates + activity due dates + budget periods
  const parseDateSafe = (s) => { try { return s ? parseISO(s) : null; } catch { return null; } };

  const allEvents = [
    ...appointments.map(a => ({
      id: `appt-${a.id}`, kind: "appointment", date: parseDateSafe(a.date), raw: a,
      title: a.title, subtitle: `${a.time || ""} · ${getProviderName(a.provider_id)}`
    })),
    ...goals.filter(g => g.target_date).map(g => ({
      id: `goal-${g.id}`, kind: "goal", date: parseDateSafe(g.target_date), raw: g,
      title: g.title, subtitle: `Goal target · ${getChildName(g.child_id)}`
    })),
    ...activities.filter(a => a.due_date).map(a => ({
      id: `act-${a.id}`, kind: "activity", date: parseDateSafe(a.due_date), raw: a,
      title: a.title, subtitle: `Activity due · ${getChildName(a.child_id)}`
    })),
    ...budgets.flatMap(b => {
      const events = [];
      const start = parseDateSafe(b.period_start || b.created_at);
      if (!start) return events;
      // Compute period end when period_end is absent
      let end = parseDateSafe(b.period_end);
      if (!end && b.period) {
        const clone = new Date(start);
        if (b.period === "monthly") clone.setMonth(clone.getMonth() + 1);
        else if (b.period === "quarterly") clone.setMonth(clone.getMonth() + 3);
        else if (b.period === "yearly") clone.setFullYear(clone.getFullYear() + 1);
        else end = null;
        if (b.period !== "one-time") end = clone;
      }
      events.push({ id: `bud-start-${b.id}`, kind: "budget_start", date: start, raw: b, title: `${b.name} starts`, subtitle: `Budget period · ${b.child_id ? getChildName(b.child_id) : "Combined"}` });
      if (end) events.push({ id: `bud-end-${b.id}`, kind: "budget_end", date: end, raw: b, title: `${b.name} ends`, subtitle: `Budget period · ${b.child_id ? getChildName(b.child_id) : "Combined"}` });
      return events;
    })
  ].filter(e => e.date);

  const filteredAppointments = allEvents.filter(e => isSameDay(e.date, selectedDate)).sort((a, b) => a.kind.localeCompare(b.kind));

  const datesWithAppointments = allEvents.map(e => e.date);

  const getEventStyle = (kind) => {
    switch (kind) {
      case "appointment": return { badge: "bg-primary/10 text-primary", label: "Appointment" };
      case "goal": return { badge: "bg-purple-100 text-purple-700", label: "Goal" };
      case "activity": return { badge: "bg-amber-100 text-amber-700", label: "Activity" };
      case "budget_start": return { badge: "bg-emerald-100 text-emerald-700", label: "Budget start" };
      case "budget_end": return { badge: "bg-rose-100 text-rose-700", label: "Budget end" };
      default: return { badge: "bg-muted text-muted-foreground", label: kind };
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: "bg-info/10 text-info",
      completed: "bg-success/10 text-success",
      cancelled: "bg-muted text-muted-foreground"
    };
    return colors[status] || "bg-muted text-muted-foreground";
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 bg-muted rounded-lg w-48" />
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="h-80 bg-muted rounded-3xl" />
          <div className="lg:col-span-2 h-80 bg-muted rounded-3xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="appointments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3">
            <CalendarIcon className="h-8 w-8" />
            Calendar
          </h1>
          <p className="text-muted-foreground mt-1">Appointments, goal target dates, activity due dates, and budget periods — all in one view</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-appointment-btn">
              <Plus className="mr-2 h-4 w-4" />
              Add Appointment
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingAppt ? "Edit Appointment" : "New Appointment"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Child</Label>
                  <Select value={form.child_id} onValueChange={(v) => setForm({ ...form, child_id: v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Select child" />
                    </SelectTrigger>
                    <SelectContent>
                      {children.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Therapist</Label>
                  <Select value={form.provider_id} onValueChange={(v) => setForm({ ...form, provider_id: v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Select therapist" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.filter(p => !form.child_id || p.child_id === form.child_id).map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Speech Therapy Session"
                  required
                  className="rounded-xl"
                  data-testid="appointment-title-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    required
                    className="rounded-xl"
                    data-testid="appointment-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={form.time}
                    onChange={(e) => setForm({ ...form, time: e.target.value })}
                    required
                    className="rounded-xl"
                    data-testid="appointment-time-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Duration (min)</Label>
                  <Input
                    type="number"
                    value={form.duration_minutes}
                    onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })}
                    min={15}
                    step={15}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(s => (
                        <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Session notes..."
                  className="rounded-xl resize-none"
                  rows={2}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm} className="flex-1 rounded-full">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 rounded-full" data-testid="save-appointment-btn">
                  {editingAppt ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Calendar & List Layout */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardContent className="p-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-2xl"
              modifiers={{
                hasAppointment: datesWithAppointments
              }}
              modifiersStyles={{
                hasAppointment: {
                  fontWeight: 'bold',
                  textDecoration: 'underline',
                  textDecorationColor: 'hsl(var(--primary))'
                }
              }}
            />
          </CardContent>
        </Card>

        {/* Appointments List */}
        <Card className="lg:col-span-2 border-0 shadow-lg rounded-3xl">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-primary" />
              {format(selectedDate, "EEEE, MMMM d, yyyy")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {filteredAppointments.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <CalendarIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Nothing scheduled on this day</p>
              </div>
            ) : (
              filteredAppointments.map((event, i) => {
                const isAppt = event.kind === "appointment";
                const style = getEventStyle(event.kind);
                const appt = event.raw;
                return (
                  <div
                    key={event.id}
                    className={`flex items-center gap-4 p-4 bg-muted/30 rounded-2xl hover:bg-muted/50 transition-colors animate-fadeIn stagger-${(i % 6) + 1}`}
                    data-testid={`calendar-item-${event.id}`}
                  >
                    <div className="h-12 w-12 rounded-xl bg-coral-100 flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 text-coral-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-heading font-bold truncate">{event.title}</h4>
                        <Badge className={`rounded-full text-[10px] font-semibold ${style.badge}`}>{style.label}</Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        {isAppt ? (
                          <>
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {appt.time} ({appt.duration_minutes}min)</span>
                            <span className="flex items-center gap-1"><User className="h-3 w-3" /> {getChildName(appt.child_id)}</span>
                            <span className="flex items-center gap-1"><Stethoscope className="h-3 w-3" /> {getProviderName(appt.provider_id)}</span>
                          </>
                        ) : (
                          <span>{event.subtitle}</span>
                        )}
                      </div>
                    </div>
                    {isAppt && (
                      <>
                        <Badge className={`rounded-full capitalize ${getStatusColor(appt.status)}`}>{appt.status}</Badge>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(appt)} className="rounded-full h-9 w-9">
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(appt.id)} className="rounded-full h-9 w-9 text-destructive hover:text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
